import Vue from "vue";
import VueRouter, { RouteConfig } from "vue-router";
import store from "@/store/index.js";
import i18n from "@/i18n.js";
import { getBrandingConfig } from "@/config";

Vue.use(VueRouter);

const routes: Array<RouteConfig> = [
  {
    path: "/auth",
    component: () => import("@/modules/auth/AuthLayout.vue"),
    meta: {
      guestOnly: true,
    },
    children: [
      {
        path: "login",
        name: "login",
        component: () => import(/* webpackChunkName: "login" */ "@/modules/auth/pages/login.page.vue"),
        meta: {
          title: "Логин",
          guestOnly: true,
        },
      },
    ],
  },
  {
    path: "/patients",
    component: () => import("@/views/layouts/PatientsLayout.vue"),
    meta: {
      authOnly: true,
    },
    children: [
      {
        path: "/cabinet",
        name: "cabinet",
        component: () => import(/* webpackChunkName: "cabinet" */ "../modules/cabinet/cabinet.page.vue"),
      },

      {
        path: "",
        name: "patients",
        component: () => import(/* webpackChunkName: "patients" */ "../modules/patients/patients.page.vue"),
        meta: {
          title: "Patients",
        },
      },
      {
        path: "/patients/calendar",
        name: "patients-calendar",
        component: () => import(/* webpackChunkName: "calendar" */ "../views/pages/calendar/Calendar.vue"),
        meta: {
          title: "Calendar",
        },
      },
      {
        path: "/patients/inspections",
        name: "patients-inspections",
        component: () => import(/* webpackChunkName: "calendar" */ "../views/pages/inspection/inspection-patients.page.vue"),
        meta: {
          title: "Inspections",
        },
      },
      {
        path: "/patients/statistics/:id",
        name: "patients-statistics",
        component: () => import(/* webpackChunkName: "statistics" */ "../views/pages/statistics/statistics.page.vue"),
        meta: {
          title: "Statistics",
        },
      },
      {
        path: "/patients/schedule/",
        name: "patients-schedule",
        component: () => import(/* webpackChunkName: "patients-schedule" */ "../modules/common-schedule/schedule.page.vue"),
        meta: {
          title: "Schedule",
        },
      },
    ],
  },
  {
    path: "/",
    component: () => import("@/views/layouts/DefaultLayout.vue"),
    meta: {
      authOnly: true,
    },
    children: [
      {
        path: "",
        name: "home",
        component: () => import(/* webpackChunkName: "home" */ "../views/pages/ikp/IKP.vue"),
      },

      {
        path: "/ikp",
        name: "IKP",
        component: () => import(/* webpackChunkName: "ikp" */ "../views/pages/ikp/IKP.vue"),
        meta: {
          title: "IKP",
        },
      },

      {
        path: "/tasks",
        name: "tasks",
        component: () => import(/* webpackChunkName: "tasks" */ "../views/pages/tasks/Tasks.vue"),
        meta: {
          title: "Tasks",
        },
      },
      {
        path: "/tasks-results",
        name: "tasks-results",
        component: () => import(/* webpackChunkName: "tasks-results" */ "../views/pages/tasks-results/TasksResults.vue"),
        meta: {
          title: "Tasks Results",
        },
      },

      {
        path: "/appointment",
        name: "appointment",
        component: () => import(/* webpackChunkName: "appointment" */ "../views/pages/appointment/AppointmentList.vue"),
        meta: {
          title: "Appointment",
        },
      },

      {
        path: "/appointment/:id",
        name: "AppointmentDetail",
        component: () => import(/* webpackChunkName: "appointment" */ "../views/pages/appointment/AppointmentDetail.vue"),
        meta: {
          title: "Appointment",
        },
      },

      {
        path: "/online-consultations",
        name: "online-consultations",
        component: () =>
          import(
            /* webpackChunkName: "online-consultations" */ "../views/pages/online-consultations/OnlineConsultationsList.vue"
          ),
        meta: {
          title: "Online consultations",
        },
      },

      {
        path: "/research",
        name: "research",
        component: () => import(/* webpackChunkName: "research" */ "../views/pages/research/Research.vue"),
        meta: {
          title: "Conducting research",
        },
      },
      {
        path: "/laboratory-research",
        name: "laboratory-research",
        component: () => import(/* webpackChunkName: "research" */ "../views/pages/laboratory-research/LaboratoryResearch.vue"),
        meta: {
          title: "Laboratory research",
        },
      },

      {
        path: "/allergy",
        name: "allergy",
        component: () => import(/* webpackChunkName: "allergy" */ "../views/pages/allergy/AllergyListPage.vue"),
        meta: {
          title: "Allergies",
        },
      },

      {
        path: "/diagnosis",
        name: "diagnosis",
        component: () => import(/* webpackChunkName: "diagnosis" */ "../views/pages/diagnosis/Diagnosis.vue"),
        meta: {
          title: "Unspecified diagnoses",
        },
      },

      {
        path: "/diseases",
        name: "diseases",
        component: () => import(/* webpackChunkName: "diseases" */ "../views/pages/diseases/DiseasesList.vue"),
        meta: {
          title: "Diseases",
        },
      },

      {
        path: "/documents",
        name: "documents",
        component: () => import(/* webpackChunkName: "documents" */ "../views/pages/documents/Documents.vue"),
        meta: {
          title: "Documents",
        },
      },

      {
        path: "/documents/:id",
        name: "document",
        component: () => import(/* webpackChunkName: "documents" */ "../views/pages/documents/Documents.vue"),
        meta: {
          title: "Documents",
        },
      },

      {
        path: "/drugs",
        name: "drugs",
        component: () => import(/* webpackChunkName: "drugs" */ "../views/pages/drugs/Drugs.vue"),
        meta: {
          title: "Medicines",
        },
      },

      {
        path: "/parameters",
        name: "parameters",
        component: () => import(/* webpackChunkName: "parameters" */ "../views/pages/parameters/Parameters.vue"),
        meta: {
          title: "Parameters",
        },
      },

      {
        path: "/previous-appeal",
        name: "previous-appeal",
        component: () => import(/* webpackChunkName: "previous-appeal" */ "../views/pages/previous-appeal/PreviousCalls.vue"),
        meta: {
          title: "Previous appeal",
        },
      },

      {
        path: "/single-clinic/:id",
        component: () => import(/* webpackChunkName: "single-clinic" */ "../views/pages/clinic/Clinic.vue"),
      },

      {
        path: "/interview",
        name: "interview",
        component: () => import(/* webpackChunkName: "interview" */ "../views/pages/interview/InterviewIndex.vue"),
        meta: {
          title: "Interview",
        },
      },
      {
        path: "/inspection",
        name: "inspection",
        component: () => import(/* webpackChunkName: "inspection" */ "../views/pages/inspection/inspection.page.vue"),
        meta: {
          title: "Inspection",
        },
      },

      {
        path: "/group-conclusion",
        name: "group-conclusion",
        component: () =>
          import(/* webpackChunkName: "group-conclusion" */ "../views/pages/group-conclusion/group-conclusion.page.vue"),
        meta: {
          title: "Group conclusion",
        },
      },
      {
        path: "/total-monthly-report",
        name: "total-monthly-report",
        component: () =>
          import(/* webpackChunkName: "total-monthly-report" */ "../views/pages/group-conclusion/total-monthly-report.page.vue"),
        meta: {
          title: "Total monthly report",
        },
      },

      {
        path: "/agreements",
        name: "agreements",
        component: () => import(/* webpackChunkName: "agreements" */ "@/modules/agreements/agreements.page.vue"),
        meta: {
          title: "Agreements",
        },
      },
      {
        path: "/qr-codes",
        name: "qr-codes",
        component: () => import(/* webpackChunkName: "qr-codes" */ "../views/pages/qr-codes/QrCodes.vue"),
        meta: {
          title: "QR-codes",
        },
      },
      {
        path: "/test",
        name: "test",
        component: () => import(/* webpackChunkName: "test" */ "../views/test.vue"),
      },
    ],
  },
  {
    path: "/print/:type",
    name: "print",
    component: () => import(/* webpackChunkName: "print" */ "../views/pages/PrintPage.vue"),
  },
];

const router = new VueRouter({
  mode: "history",
  routes,
  scrollBehavior() {
    return { x: 0, y: 0 };
  },
});

function isLoggedIn() {
  return !!store.state.auth.accessToken;
}

router.beforeEach((to, from, next) => {
  if (to.matched.some(record => record.meta.authOnly)) {
    // this route requires auth, check if logged in
    // if not, redirect to login page.
    if (!isLoggedIn()) {
      next({
        path: "/auth/login",
        query: { redirect: to.fullPath },
      });
    } else {
      next();
    }
  } else if (to.matched.some(record => record.meta.guestOnly)) {
    // this route requires auth, check if logged in
    // if not, redirect to login page.
    if (isLoggedIn()) {
      next({
        path: "/patients/",
        query: {},
      });
    } else {
      next();
    }
  } else {
    next(); // make sure to always call next()!
  }
});

router.afterEach(to => {
  // Use next tick to handle router history correctly
  // see: https://github.com/vuejs/vue-router/issues/914#issuecomment-384477609
  Vue.nextTick(() => {
    const branding = getBrandingConfig();
    document.title = to.meta && to.meta.title ? `${branding.title}: ${i18n.t(to.meta.title)}` : branding.title;
  });
});

export default router;
